'use client';

import { User, WalletWithMetadata, usePrivy } from '@privy-io/react-auth';
import {
	ConnectedStandardSolanaWallet,
	useWallets,
} from '@privy-io/react-auth/solana';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { WithChildren } from 'src/utils/UtilityTypes';
import {
	DRIFT_CUSTOM_WALLET_OPTIONS,
	isWalletBlacklistedForEmbeddedWallets,
} from 'src/constants/wallets';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useWalletContext from 'src/hooks/useWalletContext';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

export type PrivyWalletSessionMode = 'none' | 'eoa-linked' | 'privy-native';

interface PrivyWalletDetails {
	user: User | null;
	wallets: ConnectedStandardSolanaWallet[];
	eoaWallet: ConnectedStandardSolanaWallet | undefined;
	embeddedWallet: ConnectedStandardSolanaWallet | undefined;
	solanaWalletAuthorityForPrivyUser: string | undefined;
	sessionMode: PrivyWalletSessionMode;
	isPrivyNativeAuth: boolean;
}

const PrivyWalletContext = createContext<PrivyWalletDetails | null>(null);

export default function PrivyWalletProvider({ children }: WithChildren) {
	const { user } = usePrivy();
	const { wallets, ready: privyWalletsReady } = useWallets();
	const walletContext = useWalletContext();
	const currentUserAccount = useCurrentUserAccount();
	const isPrivyNativeAuthFromStore = useDriftStore(
		(s) => s.wallet.isPrivyNativeAuth
	);

	const [eoaWallet, setEoaWallet] = useState<ConnectedStandardSolanaWallet>();
	const [embeddedWallet, setEmbeddedWallet] =
		useState<ConnectedStandardSolanaWallet>();

	const solanaWalletAuthorityForPrivyUser = useMemo(
		() =>
			user?.linkedAccounts?.find(
				(account): account is WalletWithMetadata =>
					(account as WalletWithMetadata).connectorType !== 'embedded' &&
					(account as WalletWithMetadata).type === 'wallet'
			)?.address,
		[user?.linkedAccounts]
	);

	const handleSetEoaWallet = useCallback(
		(nextWallet: ConnectedStandardSolanaWallet | undefined) => {
			setEoaWallet((previousWallet) =>
				resolveWalletWithStableReference(previousWallet, nextWallet)
			);
		},
		[]
	);

	const handleSetEmbeddedWallet = useCallback(
		(nextWallet: ConnectedStandardSolanaWallet | undefined) => {
			setEmbeddedWallet((previousWallet) =>
				resolveWalletWithStableReference(previousWallet, nextWallet)
			);
		},
		[]
	);

	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;

		if (!walletAdapterName) {
			handleSetEoaWallet(undefined);
			return;
		}

		const eoaWallets = wallets.filter(
			(wallet) =>
				wallet.standardWallet.name !==
				DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME
		);

		if (eoaWallets.length === 0) {
			handleSetEoaWallet(undefined);
			return;
		}

		if (eoaWallets.length > 1) {
			const adapterWallet = eoaWallets.find(
				(wallet) => wallet.standardWallet.name === walletAdapterName
			);

			handleSetEoaWallet(adapterWallet ?? eoaWallets[0]);
			return;
		}

		handleSetEoaWallet(eoaWallets[0]);
	}, [walletContext.wallet?.adapter?.name, wallets, handleSetEoaWallet]);

	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;
		const isBlacklistedWalletAdapter =
			isWalletBlacklistedForEmbeddedWallets(walletAdapterName);

		if (isBlacklistedWalletAdapter) {
			handleSetEmbeddedWallet(undefined);
			return;
		}

		const possibleEmbeddedWallets = wallets.filter(
			(wallet) =>
				wallet.standardWallet.name ===
				DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME
		);
		const delegatedEmbeddedWallet = possibleEmbeddedWallets.find(
			(wallet) => wallet.address === currentUserAccount?.delegate.toString()
		);

		handleSetEmbeddedWallet(
			delegatedEmbeddedWallet ?? possibleEmbeddedWallets[0]
		);
	}, [
		wallets,
		currentUserAccount?.delegate?.toString(),
		handleSetEmbeddedWallet,
		walletContext.wallet?.adapter?.name,
	]);

	const sessionMode: PrivyWalletSessionMode = (() => {
		if (!user) {
			return 'none';
		}

		if (isPrivyNativeAuthFromStore) {
			return 'privy-native';
		}

		if (!privyWalletsReady) {
			// Privy may briefly hydrate user state before wallet/link metadata is ready.
			// Treat this as unresolved to avoid misclassifying EOA sessions as native.
			return 'none';
		}

		if (solanaWalletAuthorityForPrivyUser) {
			return 'eoa-linked';
		}

		return 'privy-native';
	})();

	const value = useMemo<PrivyWalletDetails>(
		() => ({
			user,
			wallets,
			eoaWallet,
			embeddedWallet,
			solanaWalletAuthorityForPrivyUser,
			sessionMode,
			isPrivyNativeAuth: sessionMode === 'privy-native',
		}),
		[
			user,
			wallets,
			eoaWallet,
			embeddedWallet,
			solanaWalletAuthorityForPrivyUser,
			sessionMode,
		]
	);

	return (
		<PrivyWalletContext.Provider value={value}>
			{children}
		</PrivyWalletContext.Provider>
	);
}

export function usePrivyWalletContext(): PrivyWalletDetails {
	const context = useContext(PrivyWalletContext);

	if (!context) {
		throw new Error(
			'usePrivyWalletContext must be used within PrivyWalletProvider'
		);
	}

	return context;
}

function resolveWalletWithStableReference(
	previousWallet: ConnectedStandardSolanaWallet | undefined,
	nextWallet: ConnectedStandardSolanaWallet | undefined
) {
	if (!previousWallet || !nextWallet) {
		return nextWallet;
	}

	if (
		previousWallet.address === nextWallet.address &&
		previousWallet.standardWallet.name === nextWallet.standardWallet.name
	) {
		return previousWallet;
	}

	return nextWallet;
}
